'use client';

import { useTranslations } from 'next-intl';
import React, { useEffect, useRef } from 'react';

import { DomainConfigurationDto } from '@/types/DomainConfigurationDto';

import { withOpacity } from '@cineamo/ui/src/helpers/color-helper';

import CenterLayout from '@cineamo/composables/src/CenterLayout';

declare global {
    interface Window {
        n2g?: ((...args: unknown[]) => void) & { q?: unknown[]; l?: number };
        Newsletter2GoTrackingObject?: string;
    }
}

type Newsletter2GoFormProps = {
    n2gId: string;
    domainConfiguration: DomainConfigurationDto;
};

export default function Newsletter2GoForm({ n2gId, domainConfiguration }: Newsletter2GoFormProps) {
    const t = useTranslations();
    const containerRef = useRef<HTMLDivElement | null>(null);
    const initializedRef = useRef(false);

    const { onBackgroundColor, primaryColor, onPrimaryColor } = domainConfiguration.style;

    useEffect(() => {
        if (initializedRef.current) return;
        initializedRef.current = true;

        // Place the marker script inside our container so n2g injects the form here.
        const markerScript = document.createElement('script');
        markerScript.id = 'n2g_script';
        containerRef.current?.appendChild(markerScript);

        // Bootstrap n2g (same IIFE as the snippet from Newsletter2Go).
        window.Newsletter2GoTrackingObject = 'n2g';
        window.n2g =
            window.n2g ||
            (((...args: unknown[]) => {
                (window.n2g!.q = window.n2g!.q || []).push(args);
            }) as Window['n2g']);
        window.n2g!.l = Number(new Date());

        if (!document.querySelector('script[src="https://static.newsletter2go.com/utils.js"]')) {
            const utils = document.createElement('script');
            utils.async = true;
            utils.src = 'https://static.newsletter2go.com/utils.js';
            document.head.appendChild(utils);
        }

        const borderColor = withOpacity(onBackgroundColor, 0.2);

        const config = {
            form: { class: '', style: `color: ${onBackgroundColor};` },
            container: { type: 'div', class: '', style: '' },
            row: { type: 'div', class: '', style: 'margin-top: 15px;' },
            columnLeft: { type: 'div', class: '', style: '' },
            columnRight: { type: 'div', class: '', style: '' },
            input: {
                class: '',
                style: `padding: 8px 12px; border-radius: 6px; border: 1px solid ${borderColor}; background-color: transparent; color: ${onBackgroundColor}; width: 100%;`
            },
            dropdown: {
                type: 'select',
                class: '',
                style: `padding: 8px 12px; border-radius: 6px; border: 1px solid ${borderColor}; background-color: transparent; color: ${onBackgroundColor};`
            },
            checkbox: {
                type: 'input',
                class: '',
                style: `accent-color: ${primaryColor};`
            },
            label: {
                type: 'label',
                class: '',
                style: `padding-left: 5px; color: ${onBackgroundColor};`
            },
            button: {
                type: 'button',
                class: '',
                style: `display: block; width: 100%; background-color: ${primaryColor}; border: none; border-radius: 8px; padding: 12px 24px; color: ${onPrimaryColor}; margin-top: 20px; cursor: pointer; font-weight: 600;`
            },
            message: {
                type: 'h2',
                class: '',
                style: `text-align: center; color: ${onBackgroundColor};`
            }
        };

        window.n2g!('create', n2gId);
        window.n2g!('subscribe:createForm', config);
    }, [n2gId, onBackgroundColor, primaryColor, onPrimaryColor]);

    return (
        <main className="min-h-screen py-24">
            <CenterLayout>
                <div className="max-w-2xl mx-auto space-y-24">
                    <h1
                        className="text-headline-32 text-center"
                        style={{ color: onBackgroundColor }}>
                        {t('newsletter.hero.title')}
                    </h1>
                    <div ref={containerRef} />
                </div>
            </CenterLayout>
        </main>
    );
}
